import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { PageHeader, Spin, message, Select, Popconfirm, Empty, Alert } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  ReactFlowProvider,
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import { ButtonItem, SelectItem } from "../../../../Components/Items";
import HabitacionCanvas from "../MapaHotel/Components/HabitacionCanvas";
import {
  construirNodosHabitaciones,
  habitacionIdDeNodo,
  ANCHO_DEFAULT,
  ALTO_DEFAULT,
} from "../MapaHotel/Components/nodosHabitaciones";
import ModalNivelHotel from "./Components/ModalNivelHotel";
import ModalAgregarHabitaciones from "./Components/ModalAgregarHabitaciones";
import PanelPropiedadesHabitacion from "./Components/PanelPropiedadesHabitacion";

const { Option } = Select;

// Línea guía de alineación: capa puramente visual, no se guarda. Se mete como un nodo más de React
// Flow para que herede gratis el pan/zoom del lienzo (mismo truco que MapaAlmacenEditor.js).
const LineaGuiaCanvas = () => (
  <div style={{ width: "100%", height: "100%", background: "#00BFD6", opacity: 0.6 }} />
);

const nodeTypes = { habitacion: HabitacionCanvas, guia: LineaGuiaCanvas };

const UMBRAL_ALINEACION = 25; // px lógicos: qué tan cerca hay que soltar para que se pegue a otra habitación
const TAMANO_LINEA_GUIA = 6000;

//Editor del mapa de un piso del hotel: dar de alta habitaciones, arrastrarlas a su lugar,
//redimensionarlas y editar sus datos. Es la versión reducida de MapaAlmacenEditor.js —aquí no hay
//grafo de circulación, ni conexiones, ni validación de rutas: solo cajas colocadas en un plano—.
//
//No pinta ocupación a propósito: configurar el plano y consultarlo son dos tareas distintas, y el
//color de "ocupada" cambiando bajo el cursor mientras se acomodan los cuartos solo estorbaría. La
//ocupación se ve en MapaHotel.js.
const MapaHotelEditor = ({ empresaId, headersToken, proyectoId, nivelHotelIdInicial, history }) => {
  const [cargando, setCargando] = useState(false);
  const [niveles, setNiveles] = useState([]);
  const [nivelHotelId, setNivelHotelId] = useState(nivelHotelIdInicial);
  const [habitacionesCrudas, setHabitacionesCrudas] = useState([]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [guiaAlineacion, setGuiaAlineacion] = useState({ x: null, y: null });
  const [seleccionIds, setSeleccionIds] = useState([]);

  const [habitacionSeleccionada, setHabitacionSeleccionada] = useState(null);
  const [modalHabitacionesVisible, setModalHabitacionesVisible] = useState(false);
  const [modalNivel, setModalNivel] = useState({ visible: false, nivel: null });

  const instanciaFlow = useRef(null);

  const nivelActual = niveles.find((valor) => String(valor._id) === String(nivelHotelId)) || null;

  const cargarNiveles = useCallback(async () => {
    if (!proyectoId) return;
    try {
      const res = await axios.post(
        "hoteleria/selectNivelesHotel",
        { empresaId, proyectoId },
        { headers: headersToken }
      );
      if (res.data.success !== 2) return;
      const nivelesCargados = res.data.niveles || [];
      setNiveles(nivelesCargados);
      setNivelHotelId((prev) => {
        const sigueExistiendo = nivelesCargados.some((valor) => String(valor._id) === String(prev));
        return sigueExistiendo ? prev : nivelesCargados[0]?._id;
      });
    } catch (error) {
      console.error("Error al cargar los pisos:", error);
      message.error("No se pudieron cargar los pisos");
    }
  }, [empresaId, proyectoId, headersToken]);

  useEffect(() => {
    cargarNiveles();
  }, [cargarNiveles]);

  const cargarHabitaciones = useCallback(async () => {
    if (!nivelHotelId) {
      setHabitacionesCrudas([]);
      return;
    }
    setCargando(true);
    try {
      // Se reusa selectMapaHotel en vez de tener un endpoint aparte para el editor: devuelve las
      // habitaciones con la descripción de su insumo, que es justo lo que hace falta para mostrarlas.
      // `incluirProgramadas: false` porque el editor no pinta ocupación de ninguna clase, y proyectar
      // las ventas programadas sería trabajo tirado a la basura.
      const res = await axios.post(
        "hoteleria/selectMapaHotel",
        { empresaId, proyectoId, nivelHotelId, incluirProgramadas: false },
        { headers: headersToken }
      );
      if (res.data.success === 2) setHabitacionesCrudas(res.data.habitaciones || []);
    } catch (error) {
      console.error("Error al cargar las habitaciones:", error);
      message.error("No se pudieron cargar las habitaciones");
    } finally {
      setCargando(false);
    }
  }, [empresaId, proyectoId, nivelHotelId, headersToken]);

  useEffect(() => {
    cargarHabitaciones();
  }, [cargarHabitaciones]);

  // Guardado de geometría (arrastre y resize). Solo manda lo que cambió: el backend aplica campo por
  // campo, así que guardar una posición no toca el número ni el insumo.
  const guardarGeometria = useCallback(
    async (habitacionId, cambios) => {
      try {
        const res = await axios.post(
          "hoteleria/editarHabitacion",
          { empresaId, _id: habitacionId, ...cambios },
          { headers: headersToken }
        );
        if (res.data.success !== 2) {
          message.error(res.data.error || "Falló el guardado de la habitación");
          return;
        }
        setHabitacionesCrudas((prev) =>
          prev.map((valor) =>
            String(valor._id) === String(habitacionId) ? { ...valor, ...cambios } : valor
          )
        );
      } catch (error) {
        console.error("Error al guardar la habitación:", error);
        message.error("Falló el guardado de la habitación");
      }
    },
    [empresaId, headersToken]
  );

  const alRedimensionar = useCallback(
    (habitacionId, params) =>
      guardarGeometria(habitacionId, { ancho: params.width, alto: params.height }),
    [guardarGeometria]
  );

  useEffect(() => {
    setNodes(
      construirNodosHabitaciones({
        habitaciones: habitacionesCrudas,
        ocupacion: [],
        editable: true,
        onResize: alRedimensionar,
      })
    );
  }, [habitacionesCrudas, alRedimensionar, setNodes]);

  // Mientras se arrastra, si el centro de la habitación queda a menos de UMBRAL_ALINEACION del centro
  // de otra, se pega a ese eje exacto y se dibuja la guía. Sin esto acomodar un pasillo de 12 cuartos
  // a ojo queda siempre torcido.
  const onNodeDrag = useCallback(
    (_event, node) => {
      const idPropio = habitacionIdDeNodo(node.id);
      const propia = habitacionesCrudas.find((valor) => String(valor._id) === idPropio);
      const anchoPropio = propia?.ancho || ANCHO_DEFAULT;
      const altoPropio = propia?.alto || ALTO_DEFAULT;

      const centroX = node.position.x + anchoPropio / 2;
      const centroY = node.position.y + altoPropio / 2;

      let mejorX = null;
      let mejorY = null;
      let distX = UMBRAL_ALINEACION;
      let distY = UMBRAL_ALINEACION;

      habitacionesCrudas.forEach((otra) => {
        if (String(otra._id) === idPropio) return;
        const centroOtraX = (otra.x || 0) + (otra.ancho || ANCHO_DEFAULT) / 2;
        const centroOtraY = (otra.y || 0) + (otra.alto || ALTO_DEFAULT) / 2;
        const dx = Math.abs(centroOtraX - centroX);
        if (dx < distX) {
          distX = dx;
          mejorX = centroOtraX;
        }
        const dy = Math.abs(centroOtraY - centroY);
        if (dy < distY) {
          distY = dy;
          mejorY = centroOtraY;
        }
      });

      setGuiaAlineacion({ x: mejorX, y: mejorY });

      if (mejorX !== null || mejorY !== null) {
        const nuevaPosicion = {
          x: mejorX !== null ? mejorX - anchoPropio / 2 : node.position.x,
          y: mejorY !== null ? mejorY - altoPropio / 2 : node.position.y,
        };
        setNodes((prev) =>
          prev.map((valor) => (valor.id === node.id ? { ...valor, position: nuevaPosicion } : valor))
        );
      }
    },
    [habitacionesCrudas, setNodes]
  );

  const onNodeDragStop = useCallback(
    (_event, node) => {
      setGuiaAlineacion({ x: null, y: null });
      // La posición que se guarda es la del estado del lienzo, no la del evento: onNodeDrag pudo
      // haberla pegado a un eje de alineación después de la última posición que reporta el drag.
      const nodoActual = nodes.find((valor) => valor.id === node.id);
      const posicion = nodoActual ? nodoActual.position : node.position;
      guardarGeometria(habitacionIdDeNodo(node.id), {
        x: Math.round(posicion.x),
        y: Math.round(posicion.y),
      });
    },
    [nodes, guardarGeometria]
  );

  const onNodeDoubleClick = useCallback(
    (_event, node) => {
      const habitacion = habitacionesCrudas.find(
        (valor) => String(valor._id) === habitacionIdDeNodo(node.id)
      );
      if (habitacion) setHabitacionSeleccionada(habitacion);
    },
    [habitacionesCrudas]
  );

  const onSelectionChange = useCallback(({ nodes: nodosSeleccionados }) => {
    setSeleccionIds((nodosSeleccionados || []).map((valor) => valor.id));
  }, []);

  const eliminarSeleccion = useCallback(async () => {
    for (const id of seleccionIds) {
      const habitacionId = habitacionIdDeNodo(id);
      try {
        const res = await axios.post(
          "hoteleria/eliminarHabitacion",
          { empresaId, _id: habitacionId },
          { headers: headersToken }
        );
        if (res.data.success === 2) {
          setHabitacionesCrudas((prev) => prev.filter((valor) => String(valor._id) !== habitacionId));
        } else {
          message.error(res.data.error || "No se pudo eliminar una habitación");
        }
      } catch (error) {
        console.error("Error al eliminar la habitación:", error);
        message.error("No se pudo eliminar una habitación");
      }
    }
    setSeleccionIds([]);
  }, [seleccionIds, empresaId, headersToken]);

  // Supr/Backspace borra lo seleccionado. Se ignora si el foco está en un campo de texto, para no
  // romper el borrado normal de texto en los modales y el panel de propiedades.
  useEffect(() => {
    const manejarTeclado = (event) => {
      const enCampoTexto =
        ["INPUT", "TEXTAREA"].includes(event.target.tagName) || event.target.isContentEditable;
      if (enCampoTexto) return;
      if ((event.key === "Delete" || event.key === "Backspace") && seleccionIds.length > 0) {
        event.preventDefault();
        eliminarSeleccion();
      }
    };
    window.addEventListener("keydown", manejarTeclado);
    return () => window.removeEventListener("keydown", manejarTeclado);
  }, [eliminarSeleccion, seleccionIds]);

  const nodesConGuias = useMemo(() => {
    const guias = [];
    if (guiaAlineacion.x !== null) {
      guias.push({
        id: "guia-x",
        type: "guia",
        position: { x: guiaAlineacion.x - 0.5, y: -TAMANO_LINEA_GUIA / 2 },
        style: { width: 1, height: TAMANO_LINEA_GUIA },
        draggable: false,
        selectable: false,
        connectable: false,
        zIndex: 999,
        data: {},
      });
    }
    if (guiaAlineacion.y !== null) {
      guias.push({
        id: "guia-y",
        type: "guia",
        position: { x: -TAMANO_LINEA_GUIA / 2, y: guiaAlineacion.y - 0.5 },
        style: { width: TAMANO_LINEA_GUIA, height: 1 },
        draggable: false,
        selectable: false,
        connectable: false,
        zIndex: 999,
        data: {},
      });
    }
    return guias.length > 0 ? [...nodes, ...guias] : nodes;
  }, [nodes, guiaAlineacion]);

  // Las nuevas habitaciones se colocan debajo de todo lo que ya hay, para que nunca nazcan encimadas
  // sobre las existentes.
  const posicionParaNuevas = useMemo(() => {
    if (habitacionesCrudas.length === 0) return { x: 40, y: 40 };
    const maxY = Math.max(
      ...habitacionesCrudas.map((valor) => (valor.y || 0) + (valor.alto || ALTO_DEFAULT))
    );
    const minX = Math.min(...habitacionesCrudas.map((valor) => valor.x || 0));
    return { x: minX, y: maxY + 60 };
  }, [habitacionesCrudas]);

  const eliminarNivel = async () => {
    try {
      const res = await axios.post(
        "hoteleria/eliminarNivelHotel",
        { empresaId, _id: nivelHotelId },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success("Piso eliminado correctamente");
        setNivelHotelId(undefined);
        cargarNiveles();
      } else {
        message.error(res.data.error || "No se pudo eliminar el piso");
      }
    } catch (error) {
      console.error("Error al eliminar el piso:", error);
      message.error("No se pudo eliminar el piso");
    }
  };

  if (!proyectoId) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 24 }}>
        <Empty description="Entra a la configuración del mapa desde la pantalla Mapa de Hotel, eligiendo primero un hotel.">
          <ButtonItem className="botonAgregar" onClick={() => history.push("/panel/mapaHotel")}>
            Ir al mapa
          </ButtonItem>
        </Empty>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Configuración del mapa de hotel"
        onBack={() => history.push("/panel/mapaHotel", { proyectoId, nivelHotelId })}
        backIcon={<ArrowLeftOutlined />}
        style={{ border: "1px solid rgb(235,237,240)", backgroundColor: "white", marginBottom: 10 }}
      />

      <div style={{ background: "#fff", borderRadius: 14, padding: 16 }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-end",
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Piso</div>
            <SelectItem
              width={220}
              placeholder="Piso"
              value={nivelHotelId}
              onChange={setNivelHotelId}
            >
              {niveles.map((valorNivel) => (
                <Option key={valorNivel._id} value={valorNivel._id}>
                  {valorNivel.nombre}
                </Option>
              ))}
            </SelectItem>
          </div>

          <ButtonItem
            size="small"
            className="botonAgregar"
            icon={<PlusOutlined />}
            onClick={() => setModalNivel({ visible: true, nivel: null })}
          >
            Agregar piso
          </ButtonItem>

          <ButtonItem
            blanco
            size="small"
            icon={<EditOutlined />}
            disabled={!nivelActual}
            onClick={() => setModalNivel({ visible: true, nivel: nivelActual })}
          >
            Editar piso
          </ButtonItem>

          <Popconfirm
            title="¿Eliminar este piso? Solo se puede si ya no tiene habitaciones."
            onConfirm={eliminarNivel}
            okText="Eliminar"
            cancelText="Cancelar"
            disabled={!nivelActual}
          >
            <ButtonItem danger size="small" icon={<DeleteOutlined />} disabled={!nivelActual}>
              Eliminar piso
            </ButtonItem>
          </Popconfirm>

          <div style={{ marginLeft: "auto" }}>
            <ButtonItem
              className="botonAgregar"
              icon={<PlusOutlined />}
              disabled={!nivelHotelId}
              onClick={() => setModalHabitacionesVisible(true)}
            >
              Agregar habitaciones
            </ButtonItem>
          </div>
        </div>

        {niveles.length === 0 ? (
          <Empty description="Empieza creando el primer piso del hotel" style={{ padding: 60 }}>
            <ButtonItem
              className="botonAgregar"
              icon={<PlusOutlined />}
              onClick={() => setModalNivel({ visible: true, nivel: null })}
            >
              Agregar piso
            </ButtonItem>
          </Empty>
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Arrastra las habitaciones para acomodarlas; se alinean solas cuando quedan cerca de otra. Selecciona una y usa el tirador de la esquina para cambiarle el tamaño. Doble clic abre sus datos, y Supr borra lo seleccionado."
            />

            <Spin spinning={cargando}>
              <div style={{ height: "70vh", border: "1px solid #eef0f6", borderRadius: 8 }}>
                <ReactFlowProvider>
                  <ReactFlow
                    nodes={nodesConGuias}
                    edges={[]}
                    nodeTypes={nodeTypes}
                    nodesConnectable={false}
                    selectionKeyCode="Shift"
                    onNodesChange={onNodesChange}
                    onNodeDrag={onNodeDrag}
                    onNodeDragStop={onNodeDragStop}
                    onNodeDoubleClick={onNodeDoubleClick}
                    onSelectionChange={onSelectionChange}
                    onInit={(instancia) => (instanciaFlow.current = instancia)}
                    zoomOnDoubleClick={false}
                    minZoom={0.1}
                    fitView
                    fitViewOptions={{ padding: 0.3 }}
                  >
                    <Background />
                    <Controls />
                    <MiniMap />
                  </ReactFlow>
                </ReactFlowProvider>
              </div>
            </Spin>
          </>
        )}
      </div>

      <ModalNivelHotel
        visible={modalNivel.visible}
        nivel={modalNivel.nivel}
        empresaId={empresaId}
        proyectoId={proyectoId}
        headersToken={headersToken}
        onClose={() => setModalNivel({ visible: false, nivel: null })}
        onGuardado={(nivelId) => {
          setNivelHotelId(nivelId);
          cargarNiveles();
        }}
      />

      <ModalAgregarHabitaciones
        visible={modalHabitacionesVisible}
        empresaId={empresaId}
        proyectoId={proyectoId}
        nivelHotelId={nivelHotelId}
        posicionInicial={posicionParaNuevas}
        headersToken={headersToken}
        onClose={() => setModalHabitacionesVisible(false)}
        onCreadas={() => cargarHabitaciones()}
      />

      <PanelPropiedadesHabitacion
        visible={!!habitacionSeleccionada}
        habitacion={habitacionSeleccionada}
        niveles={niveles}
        empresaId={empresaId}
        headersToken={headersToken}
        onClose={() => setHabitacionSeleccionada(null)}
        onGuardado={() => {
          setHabitacionSeleccionada(null);
          cargarHabitaciones();
        }}
        onEliminado={() => {
          setHabitacionSeleccionada(null);
          cargarHabitaciones();
        }}
      />
    </>
  );
};

const mapStateToProps = (state, ownProps) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  // Igual que MapaAlmacenEditor: el hotel y el piso llegan por el state del history.push que hace
  // MapaHotel.js, no por la URL.
  proyectoId: ownProps.location?.state?.proyectoId,
  nivelHotelIdInicial: ownProps.location?.state?.nivelHotelId,
});

export default connect(mapStateToProps)(MapaHotelEditor);
